/**
 * Hook for capturing form validation errors to Sentry.
 * Only captures errors when form is submitted (not during typing).
 *
 * @example
 * ```tsx
 * const form = useForm({ ... });
 * useCaptureFormValidationErrors(form, { feature: "login-username-password" });
 * ```
 *
 * @example With useRHF:
 * ```tsx
 * const { formState, watch } = useRHF(...);
 * useCaptureFormValidationErrors({ formState, watch }, { feature: "register" });
 * ```
 */

import * as Sentry from "@sentry/react";
import logger from "@utils/logger";
import { useEffect, useRef } from "react";
import type { FieldErrors, FieldValues, UseFormReturn, UseFormWatch } from "react-hook-form";

export type TrackingFeature =
  | "login-username-password"
  | "login-phone-otp"
  | "register"
  | "forgot-password"
  | "change-password"
  | "legal-name-update"
  | "bank-account-update"
  | "add-bank-dialog"
  | "add-e-wallet"
  | "add-funding-profile"
  | "deposit-step-topup"
  | "withdraw-form"
  | "mega-share-giver-form";

export interface UseCaptureFormValidationErrorsOptions {
  feature?: TrackingFeature;
}

/**
 * Form object type - accepts either full UseFormReturn or partial with formState and watch
 */
type FormObject<TFieldValues extends FieldValues = FieldValues> =
  | UseFormReturn<TFieldValues>
  | Pick<UseFormReturn<TFieldValues>, "formState" | "watch">;

const SENSITIVE_FIELDS: readonly string[] = [
  "password",
  "pin",
  "pinCode",
  "pinCodeConfirm",
  "pinCodeNew",
  "pinCodeConfirmNew",
  "newPassword",
  "confirmNewPassword",
  "secret",
  "sms",
  "code",
];

/**
 * Checks if there are any validation errors
 */
function hasValidationErrors(errors: FieldErrors<any>): boolean {
  return Object.keys(errors).length > 0 && Object.values(errors).some((error) => error !== null && error !== undefined);
}

/**
 * Masks sensitive field values
 */
function maskSensitiveValue(fieldName: string, value: unknown): unknown {
  return SENSITIVE_FIELDS.includes(fieldName) ? "[REDACTED]" : value;
}

/**
 * Normalizes error message for use in Sentry tags
 * Truncates to 200 characters (Sentry tag limit) and normalizes common patterns
 */
function normalizeErrorMessageForTag(message: string): string {
  // Normalize common error messages for better grouping
  const normalized = message.toLowerCase().trim();

  // Replace spaces and special chars that might cause issues in tags
  const sanitized = normalized
    .replace(/[^\w\s-]/g, "") // Remove special chars except word chars, spaces, hyphens
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .substring(0, 200); // Truncate to Sentry's tag limit

  return sanitized || "unknown_error";
}

/**
 * Prepares error data for Sentry capture
 */
function getFieldValue<T extends Record<string, unknown>>(values: T | null | undefined, key: string): unknown {
  if (!values || typeof values !== "object") {
    return undefined;
  }
  return key in values ? values[key] : undefined;
}

function prepareErrorData<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>,
  watch?: UseFormWatch<TFieldValues>,
): Record<string, { message: string; value?: unknown }> {
  const currentValues = watch ? watch() : null;
  const result: Record<string, { message: string; value?: unknown }> = {};

  Object.entries(errors).forEach(([key, error]) => {
    const errorMessage = error?.message ?? "Unknown error";
    const fieldValue = currentValues ? getFieldValue(currentValues, key) : undefined;

    result[key] = {
      message: String(errorMessage),
      ...(watch && {
        value: maskSensitiveValue(key, fieldValue),
      }),
    };
  });

  return result;
}

/**
 * Captures form validation errors to Sentry when form is submitted with errors.
 * Only captures on form submission (when submitCount increases), not during typing.
 *
 * @param form - Form object from react-hook-form (useForm) or useRHF, must include formState and watch
 * @param options - Configuration options
 * @param options.feature - Feature tag for tracking which form had errors
 */
function useCaptureFormValidationErrors<TFieldValues extends FieldValues = FieldValues>(
  form: FormObject<TFieldValues>,
  options: UseCaptureFormValidationErrorsOptions = {},
) {
  const { feature } = options;
  const { formState, watch } = form;
  const submitCount = formState.submitCount;
  const previousSubmitCountRef = useRef<number>(submitCount);

  useEffect(() => {
    // Only capture when submitCount increases (form was submitted)
    const submitCountIncreased = submitCount > previousSubmitCountRef.current;

    if (!submitCountIncreased) {
      return;
    }

    // Update the ref to track the new submitCount
    previousSubmitCountRef.current = submitCount;

    // Read errors from formState at the time of submission
    // This ensures we capture errors that exist when the form is submitted,
    // not errors that change during typing
    const errors = formState.errors;

    // Only capture if there are validation errors
    if (!hasValidationErrors(errors)) {
      return;
    }

    // Prepare error data
    const errorData = prepareErrorData(errors, watch);

    // Generate unique ID for this submission attempt
    const submissionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fieldNames = Object.keys(errorData);
    const featureName = feature || "unknown";

    // In development, log to console; in production, capture to Sentry
    logger._console.group("🚨 Form Validation Failed");
    logger._console.log("Feature:", featureName);
    logger._console.log("Submit Count:", submitCount);
    logger._console.log("Submission ID:", submissionId);
    logger._console.log("Errors:", errorData);
    logger._console.groupEnd();

    // Capture to Sentry in production/staging - one event per field error
    Object.entries(errorData).forEach(([fieldName, errorInfo]) => {
      const errorMessageTag = normalizeErrorMessageForTag(errorInfo.message);

      Sentry.captureMessage("Form validation failed", {
        level: "warning",
        extra: {
          [fieldName]: errorInfo,
          allErrors: errorData,
          submissionId,
        },
        tags: {
          feature: featureName,
          field_name: fieldName,
          form_field: `${featureName}:${fieldName}`,
          field_count: String(fieldNames.length),
          submission_id: submissionId,
          error_message: errorMessageTag,
        },
      });

      logger._console.log("Tags:", {
        feature: featureName,
        field_name: fieldName,
        form_field: `${featureName}:${fieldName}`,
        field_count: String(fieldNames.length),
        submission_id: submissionId,
        error_message: errorMessageTag,
      });
    });
  }, [submitCount, formState, feature, watch]);
}

export default useCaptureFormValidationErrors;
